import { apiGet } from './apiClient';
import { sanitizeHeroPayload } from '../utils/heroRecordUtils';

const HERO_ID = 'home';

const parseEventSlotIndices = (events) => {
  if (!events || typeof events !== 'object') return [];
  return Object.keys(events)
    .map((key) => {
      const match = /^event(\d+)$/.exec(key);
      return match ? Number(match[1]) : null;
    })
    .filter((n) => n != null && Number.isFinite(n))
    .sort((a, b) => a - b);
};

const getNextSlotIndex = (events) => {
  const indices = parseEventSlotIndices(events);
  const max = indices.reduce((m, i) => Math.max(m, i), 0);
  return max + 1;
};

const slotHasEvent = (slot, eventId) => {
  if (!slot || typeof slot !== 'object') return false;
  return String(slot.id || slot.eventId || '') === String(eventId);
};

export const findEventSlotKey = (events, eventId) => {
  if (!events || typeof events !== 'object') return null;
  for (const [key, slot] of Object.entries(events)) {
    if (/^event\d+$/.test(key) && slotHasEvent(slot, eventId)) return key;
  }
  return null;
};

export const sectionContainsEvent = (section, eventId) =>
  Boolean(findEventSlotKey(section?.events, eventId));

export const heroContainsEvent = (hero, eventId) =>
  Array.isArray(hero?.specialEvents) &&
  hero.specialEvents.some((row) => String(row?.eventId || row?.id || '') === String(eventId));

export const addEventToHomeSection = (section, eventId, featuredImage = null) => {
  const events = { ...(section.events || {}) };
  const existingKey = findEventSlotKey(events, eventId);
  if (existingKey) {
    const prev = events[existingKey] || {};
    events[existingKey] = {
      ...prev,
      id: eventId,
      ...(featuredImage ? { featuredImage } : {}),
    };
  } else {
    const next = getNextSlotIndex(events);
    events[`event${next}`] = {
      id: eventId,
      ...(featuredImage ? { featuredImage } : {}),
    };
  }
  return {
    ...section,
    events,
    // Manuel slot kullanıldığı için otomatik yaklaşan listesini kapat
    fetchUpcoming: false,
  };
};

export const removeEventFromHomeSection = (section, eventId) => {
  const events = { ...(section.events || {}) };
  const key = findEventSlotKey(events, eventId);
  if (!key) return section;
  delete events[key];
  return { ...section, events };
};

export const addEventToHero = (hero, eventId, featuredImage = null) => {
  const base = hero && typeof hero === 'object' ? { ...hero } : { id: HERO_ID, specialEvents: [] };
  const specialEvents = Array.isArray(base.specialEvents) ? [...base.specialEvents] : [];
  const idx = specialEvents.findIndex(
    (row) => String(row?.eventId || row?.id || '') === String(eventId),
  );
  const row = {
    eventId,
    ...(featuredImage ? { featuredImage } : {}),
  };
  if (idx >= 0) {
    specialEvents[idx] = {
      ...specialEvents[idx],
      ...row,
      featuredImage: featuredImage || specialEvents[idx].featuredImage,
    };
    if (!featuredImage && !specialEvents[idx].featuredImage) {
      delete specialEvents[idx].featuredImage;
    }
  } else {
    specialEvents.push(row);
  }
  return sanitizeHeroPayload({ ...base, specialEvents }, HERO_ID);
};

export const removeEventFromHero = (hero, eventId) => {
  if (!hero) return null;
  const specialEvents = (Array.isArray(hero.specialEvents) ? hero.specialEvents : []).filter(
    (row) => String(row?.eventId || row?.id || '') !== String(eventId),
  );
  return sanitizeHeroPayload({ ...hero, specialEvents }, HERO_ID);
};

/** Panel update için meta alanları düş. */
const stripSectionMeta = (section) => {
  if (!section || typeof section !== 'object') return section;
  const next = { ...section };
  delete next.createdby;
  delete next.updatedby;
  delete next.createdate;
  delete next.lastupdate;
  return next;
};

const hasSelectedCategory = (section) => {
  const c = section?.category;
  if (c == null || c === '' || c === false) return false;
  if (Array.isArray(c)) return c.filter(Boolean).length > 0;
  return true;
};

/**
 * Yer seçiminde gösterilecek section'lar:
 * kategori seçili olanlar hariç (onlar otomatik kategori listesi).
 * Hero ayrı; burada sadece "özel" / manuel section'lar.
 */
export const isAssignableHomeSection = (section) => {
  if (!section) return false;
  if (hasSelectedCategory(section)) return false;
  return true;
};

/** Tüm homeSections + hero kaydı ve bu etkinliğin mevcut yerleşimleri. */
export const fetchHomePlacementState = async (eventId) => {
  const [sectionsRes, hero] = await Promise.all([
    apiGet('homeSections?page=1&perPage=200&sort=order&order=asc'),
    apiGet(`heros/${HERO_ID}`).catch(() => null),
  ]);

  const allSections = Array.isArray(sectionsRes?.data) ? sectionsRes.data : [];
  // Kategori seçili section'lar listede yok — sadece özeller
  const sections = allSections.filter(isAssignableHomeSection);
  const selectedSectionIds = sections
    .filter((s) => sectionContainsEvent(s, eventId))
    .map((s) => s.id);
  const inHero = heroContainsEvent(hero, eventId);

  const sectionFeaturedImages = {};
  sections.forEach((s) => {
    const key = findEventSlotKey(s.events, eventId);
    if (key && s.events?.[key]?.featuredImage) {
      sectionFeaturedImages[s.id] = s.events[key].featuredImage;
    }
  });

  let heroFeaturedImage = null;
  if (inHero && Array.isArray(hero?.specialEvents)) {
    const row = hero.specialEvents.find(
      (e) => String(e?.eventId || e?.id || '') === String(eventId),
    );
    heroFeaturedImage = row?.featuredImage || null;
  }

  return {
    sections,
    hero,
    selectedSectionIds,
    inHero,
    sectionFeaturedImages,
    heroFeaturedImage,
  };
};

/**
 * Seçilen section + hero durumunu uygular (dataProvider → görsel upload dahil).
 */
export const applyHomePlacements = async (
  dataProvider,
  {
    eventId,
    sections,
    hero,
    selectedSectionIds,
    inHero,
    sectionFeaturedImages = {},
    heroFeaturedImage = null,
  },
) => {
  const selectedSet = new Set((selectedSectionIds || []).map(String));
  const updates = [];

  for (const section of sections || []) {
    const id = String(section.id);
    const currently = sectionContainsEvent(section, eventId);
    const should = selectedSet.has(id);

    if (should && !currently) {
      const next = stripSectionMeta(
        addEventToHomeSection(section, eventId, sectionFeaturedImages[id] || null),
      );
      updates.push(
        dataProvider.update('homeSections', { id: section.id, data: next, previousData: section }),
      );
    } else if (should && currently) {
      const featured = sectionFeaturedImages[id];
      if (featured) {
        const next = stripSectionMeta(addEventToHomeSection(section, eventId, featured));
        updates.push(
          dataProvider.update('homeSections', {
            id: section.id,
            data: next,
            previousData: section,
          }),
        );
      }
    } else if (!should && currently) {
      const next = stripSectionMeta(removeEventFromHomeSection(section, eventId));
      updates.push(
        dataProvider.update('homeSections', { id: section.id, data: next, previousData: section }),
      );
    }
  }

  if (inHero) {
    const nextHero = addEventToHero(hero || { id: HERO_ID }, eventId, heroFeaturedImage);
    updates.push(
      dataProvider.update('heros', {
        id: HERO_ID,
        data: nextHero,
        previousData: hero || { id: HERO_ID },
      }),
    );
  } else if (heroContainsEvent(hero, eventId)) {
    const nextHero = removeEventFromHero(hero, eventId);
    if (nextHero) {
      updates.push(
        dataProvider.update('heros', {
          id: HERO_ID,
          data: nextHero,
          previousData: hero,
        }),
      );
    }
  }

  await Promise.all(updates);
};
